import { type Moment } from "moment";
import moment from "moment/moment";
import { type ComponentType, isColumnComponentType } from "#/Common/Forms/DragDrop/DragDropTypes.ts";
import { TextReplacement } from "#/Common/Forms/DragDrop/InternalComponents/Text/TextReplacement.ts";
import type { ServerSaleStatus } from "#/Data/_Bases/BaseSalesRepository.ts";
import { determineDataSource } from "#/Data/DataStores/DataSource.ts";
import { type SaleState, type SaleTotals } from "#/Reducers/SellScreen/SellScreenSaleReducerTypes.ts";
import {
    type ComponentDifference,
    getComponentDifferences,
} from "#/Routes/SellScreen/Transaction/ProductLineComponents.ts";
import { isRecord } from "../../../types/internal/utility";
import { type GiftCardUsedType } from "../../Actions/SellScreen/SellScreenActions";
import { hasColumnsValue } from "../../Common/Forms/DragDrop/ReorderableTableSettings";
import { type HeadingValueType, isHeadingValue } from "../../Common/Receipts/Components/ReceiptHeadingComponent";
import { isTaxIndicatorValue } from "../../Common/Receipts/Components/ReceiptTaxIndicatorComponent";
import { getTaxIndicators } from "../../Common/Receipts/Receipt";
import type BaseDataSource from "../../Data/_Bases/BaseDataSource";
import NotFoundException from "../../Routes/SellScreen/Exceptions/Data/NotFoundException";
import { currency } from "../Generic/Numbers";
import { caseText } from "../Generic/Strings";
import { getOption, isTrainingMode } from "../Platform/Options/Options";
import { convertFromIndexedDBToGraphQL } from "./Customers/ConvertCustomers";
import { getUserFromServer } from "./Users/UserUtilities";

export interface AdditionalSaleData {
    registerName: string;
    outletId: string;
    outletName: string;
}

interface ReceiptTextFields {
    completedAt: Moment;
    user: null | {
        name: string;
    };
    outlet: null | {
        name: string;
        businessNumber: string;
        contact: {
            addressStreet1: string;
            addressStreet2: string;
            addressSuburb: string;
            addressCity: string;
            addressPostcode: string;
            addressRegion: string;
            addressCountry: string;
            addressState: string;
            email: string;
            facebook: string;
            fax: string;
            mobile: string;
            phone: string;
            twitter: string;
            website: string;
        };
    };
    register: null | {
        name: string;
    };
    customer: null | ReceiptCustomerTextFields;
    note: string;
    currentTimestamp: Moment;
}

export interface ReceiptCustomerTextFields {
    firstName: string;
    lastName: string;
    name: string;
    code: string;
    company: string;
    businessNumber: string;
    birthday: string | null;
    gender: string;
    emails: Array<string>;
    fax: string;
    phone: string;
    mobile: string;
    website: string;
    twitter: string;
    facebook: string;
    billing: {
        addressStreet1: string;
        addressStreet2: string;
        addressSuburb: string;
        addressCity: string;
        addressPostcode: string;
        addressRegion: string;
        addressCountry: string;
        addressState?: string;
    };
    delivery: {
        addressStreet1: string;
        addressStreet2: string;
        addressSuburb: string;
        addressCity: string;
        addressPostcode: string;
        addressRegion: string;
        addressCountry: string;
        addressState?: string;
    };
    invoiceMessage: string;
    comments: string;
    customerGroup: null | {
        name: string;
    };
    loyaltyEnabled: boolean;
    loyaltyRate: null | number;
    loyaltyPoints: number;
    disablePromotions: boolean;
    priceList: null | {
        name: string;
    };
    accountLimit: null | number;
    currentOwing: number;
    requireOrderReference: boolean;
}

interface ReceiptSaleTextFields extends ReceiptTextFields {
    invoiceNo: string;
    orderReference: string;
    note: string;
    sale: SaleState;
    inTrainingMode: boolean;
    isReprint: boolean;
}

interface ReceiptPaymentTextFields extends ReceiptTextFields {
    status: "complete" | "incomplete" | "cancelled";
    reference: string;
    payment_method: {
        name: string;
    };
}

export interface ReceiptSaleData {
    status: ServerSaleStatus;
    invoiceNo: string;
    logo: string | null;
    products: Array<{
        product: string;
        "case-qty": number;
        qty: number;
        "total-items": number;
        "item-price": string;
        "case-price": string;
        discount: string;
        price: string;
        "discount-reason": string;
        tax: string;
        note: string;
        components: Array<ComponentDifference>;
        "normal-price": string;
        savings: string;
        surcharge: string;
    }>;
    tax: Array<{
        id: string;
        tax: string;
        total: string;
    }>;
    indicators: Record<string, boolean>;
    payments: Array<{
        payment: string;
        amount: string;
    }>;
    loyalty: {
        "current-loyalty": boolean | number;
        "earned": boolean | number;
        "spent": boolean | number;
        "before-sale": boolean | number;
        "after-sale": boolean | number;
    };
    account: {
        "start-balance": boolean | number;
        "end-balance": boolean | number;
        "changed-balance": boolean | number;
        "current-balance": boolean | number;
    };
    total: SaleTotals & {
        rounding: number;
    };
    surcharges: Array<{
        indicator?: string;
        name: string;
        amount: string;
    }>;
    giftCards: Array<GiftCardUsedType>;
    text: ReceiptSaleTextFields;
    eftpos: Array<string> | string;
}

export interface ReceiptPaymentData {
    text: ReceiptPaymentTextFields;
    invoices: Array<{
        timestamp: string;
        "invoice-id": string;
        total: string;
        remaining?: string;
        allocated?: string;
    }>;
    payments: Array<{
        payment: string;
        amount: string;
    }>;
    total: {
        total: number;
        invoiceCount: number;
        outstanding: number;
        rounding?: number;
        change?: number;
    };
    eftpos: Array<string> | string;
}

type ReceiptTextAlignment = "left" | "right" | "center";

class ReceiptHelper {
    protected static getPaymentMethod(method: string, source: BaseDataSource): Promise<{
        name: string;
        type?: string;
    }> {
        return new Promise((res, rej) => {
            if(method === "account") {
                rej();
            } else if(method === "loyalty") {
                res({
                    name: getOption("outlet_loyalty_name"),
                });
            } else {
                source.paymentMethods.get(method)
                    .then((paymentMethod: any) => {
                        res(paymentMethod);
                    })
                    .catch(() => {
                        res({
                            name: "Deleted Payment Method",
                        });
                    });
            }
        });
    }

    public static async createReceiptDataFromSaleState(
        sale: Required<SaleState> & { giftCardsUsed?: Array<GiftCardUsedType>; },
        additionalSaleData?: AdditionalSaleData,
        isReprint = false
    ): Promise<ReceiptSaleData> {
        const source = await determineDataSource();

        const promises: Array<Promise<unknown>> = [];

        let logo: string | null = null;

        if(sale.registerId) {
            try {
                const register = await source.registers.get(sale.registerId);
                const outlet = await source.outlets.get(register.outlet_id);

                logo = outlet.logo;
            } catch {
                // Ignore
            }
        }

        const surchargeNames: Record<number, string> = {};

        for(const rule of sale.surchargeRules) {
            surchargeNames[rule.id] = rule.name;
        }

        let surchargeKey = 1;
        const surchargeMap: Record<number, {
            indicator: string;
            name: string;
            amount: number;
        }> = {};

        const results: ReceiptSaleData = {
            status    : sale.status,
            invoiceNo : sale.invoiceId,
            products  : [],
            tax       : [],
            indicators: {},
            payments  : [],
            loyalty   : {
                "current-loyalty": false,
                "earned"         : false,
                "spent"          : false,
                "before-sale"    : false,
                "after-sale"     : false,
            },
            account: {
                "start-balance"  : false,
                "end-balance"    : false,
                "changed-balance": false,
                "current-balance": false,
            },
            total: {
                ...sale.totals,
                rounding: 0,
            },
            text: {
                invoiceNo       : "",
                orderReference  : "",
                completedAt     : moment(),
                user            : null,
                outlet          : null,
                register        : null,
                customer        : null,
                note            : "",
                currentTimestamp: moment(),
                inTrainingMode  : isTrainingMode(),
                isReprint,
                sale,
            },
            surcharges: [],
            giftCards : sale.giftCardsUsed ?? [],
            eftpos    : [],
            logo,
        };

        let loyaltyEarned = 0;
        let loyaltyRedeemed = 0;

        // Prepare payments results
        let consolidateAmount = 0;
        let hasChange = false;

        let accountUsed = 0;
        const payments = sale.payments;

        for(let i = 0; i < payments.length; i++) {
            const payment = payments[i];
            let shouldIncrement = true;

            if(payment.status === "cancelled") {
                continue;
            }

            if(payment.status === "failed") {
                shouldIncrement = false;
            }

            if(payment.method === "account") {
                accountUsed += payment.amount;
                continue;
            }

            const paymentMethod = await this.getPaymentMethod(payment.method, source);

            if(
                !hasChange &&
                paymentMethod.type === "cash" &&
                payment.status === "completed" &&
                payment.amount < 0
            ) {
                if(payment.amount * -1 === results.total.change) {
                    hasChange = true;
                    continue;
                }
            }

            let name = paymentMethod.name;

            if(payment.metadata) {
                if(payment.metadata !== "") {
                    const md = JSON.parse(payment.metadata);

                    if(md.loyalty_points) {
                        loyaltyRedeemed += md.loyalty_points;
                    }

                    if(typeof md.name?.display?.pre === "string") {
                        name = `${md.name.display.pre} ${name}`;
                    }

                    if(typeof md.name?.display?.post === "string") {
                        name = `${name} ${md.name.display.post}`;
                    }
                }
            }

            if(payment.receipt) {
                (results.eftpos as Array<string>).push(payment.receipt);
            }

            consolidateAmount += payment.amount;

            // Also have to add the cashout amount to the consolidated amount
            consolidateAmount += payment.cashout;

            // Check if the next payment can be consolidated
            let process = false;

            // TODO ADD OPTION TO TURN OFF
            if(shouldIncrement) {
                if(i === payments.length - 1) {
                    process = true;
                } else {
                    const next = payments[i + 1];

                    process = !!payment.cashout ||
                        !(
                            next.method === payment.method &&
                            !next.cashout &&
                            Math.sign(next.amount) === Math.sign(payment.amount) &&
                            payment.metadata === next.metadata
                        );
                }
            } else {
                process = true;
            }

            if(process) {
                let status: string = payment.status;

                if(status === "completed") {
                    status = "Tendered";

                    if(typeof payment.rounding === "number") {
                        results.total.rounding += payment.rounding;
                    }
                }

                if(status === "failed") {
                    status = "Declined";
                }

                status = status[0].toUpperCase() + status.slice(1).toLowerCase();

                results.payments.push({
                    payment: status + " " + name,
                    amount : currency(consolidateAmount),
                });

                if(payment.cashout) {
                    results.payments.push({
                        payment: "Cash Out",
                        amount : currency(payment.cashout),
                    });
                }

                consolidateAmount = 0;
            }
        }

        const roundTo = getOption("round_to");
        const remaining = Math.round((results.total.remaining + results.total.change) * 100) / 100;

        if(Math.abs(remaining) < roundTo) {
            results.total.rounding -= remaining;
        }

        if(isNaN(loyaltyRedeemed)) {
            loyaltyRedeemed = 0;
        }

        // Prepare for tax rates total
        const taxRates: Record<string, number> = {};

        // Prepare products results
        const products = sale.products;
        let productLoyaltyPoints = 0;
        let productRedeemPoints = 0;

        for(let i = 0; i < products.length; i++) {
            const product = products[i];

            const taxRate = product.tax.id ? product.tax.id : "";

            let discount = 0;
            let savings = 0;
            const unlocked = Math.round(products[i].prices.unlockedPrice * 100) / 100;
            const normal = Math.round(products[i].prices.normal * 100) / 100;

            if(products[i].type !== "Voucher" && !products[i].priceList && !products[i].special) {
                const actual = Math.round(products[i].prices.price * 100) / 100;

                if(normal !== actual || unlocked !== actual) {
                    if(products[i].edited && products[i].canUnlock) {
                        discount += unlocked - actual;
                    } else if(!products[i].special) {
                        savings += normal - actual;
                    }
                }
            }

            let components: Array<ComponentDifference> = [];

            if(products[i].type === "Package") {
                components = getComponentDifferences(
                    product.defaultProducts,
                    product.products,
                    product.quantity,
                    false
                );
            }

            const originalPrice = product.prices.price - product.prices.additionalPrice[1];
            const itemPrice = originalPrice / product.quantity;

            let surchargeIndicator = "";

            if(product.surcharge) {
                if(product.surcharge.id in surchargeMap) {
                    surchargeMap[product.surcharge.id].amount += product.surcharge.amount;
                    surchargeIndicator = surchargeMap[product.surcharge.id].indicator;
                } else {
                    surchargeIndicator = surchargeKey.toString();
                    surchargeKey++;

                    surchargeMap[product.surcharge.id] = {
                        indicator: surchargeIndicator,
                        name     : surchargeNames[product.surcharge.id] ?? "Surcharge",
                        amount   : product.surcharge.amount,
                    };
                }
            }

            let name = product.name;
            let quantity = product.quantity;
            let casePrice = "";

            if(product.isCase) {
                name += ` (${caseText()})`;
                quantity = Math.floor(quantity / product.caseQuantity);

                casePrice = currency(itemPrice * product.caseQuantity);
            }

            if(typeof product.metaData?.name?.display?.pre === "string") {
                name = `${product.metaData.name.display.pre} ${name}`;
            }

            if(typeof product.metaData?.name?.display?.post === "string") {
                name = `${name} ${product.metaData.name.display.post}`;
            }

            results.products.push({
                "product"        : name,
                "case-qty"       : product.caseQuantity,
                "qty"            : quantity,
                "total-items"    : product.quantity,
                "item-price"     : currency(itemPrice),
                "case-price"     : casePrice,
                "discount"       : currency(discount),
                "price"          : currency(originalPrice),
                "discount-reason": product.discountReason || "",
                "tax"            : taxRate,
                "note"           : product.note,
                "components"     : components,
                "normal-price"   : currency(normal),
                "savings"        : currency(savings),
                "surcharge"      : surchargeIndicator,
            });

            results.indicators[taxRate] = true;

            productLoyaltyPoints += product.loyalty.earn;
            productRedeemPoints += product.loyalty.redeem;

            if(product.tax.id) {
                if(!taxRates[product.tax.id]) {
                    taxRates[product.tax.id] = 0;
                }

                taxRates[product.tax.id] += product.prices.price - product.prices.price / (
                    (100 + product.tax.amount) / 100
                );
            }
        }

        if(isNaN(productLoyaltyPoints)) {
            productLoyaltyPoints = 0;
        }

        if(isNaN(productRedeemPoints)) {
            productRedeemPoints = 0;
        }

        if(productRedeemPoints !== 0) {
            loyaltyEarned = loyaltyRedeemed === 0 ?
                productLoyaltyPoints :
                productLoyaltyPoints - (
                    loyaltyRedeemed / productRedeemPoints * productLoyaltyPoints
                );
        } else {
            loyaltyEarned = productLoyaltyPoints;
        }

        // Prepare tax results
        for(const tax in taxRates) {
            if(Object.prototype.hasOwnProperty.call(taxRates, tax)) {
                promises.push(
                    source.taxRates.get(tax)
                        .then(taxRate => {
                            results.tax.push({
                                id   : taxRate.uuid,
                                tax  : taxRate.name,
                                total: currency(taxRates[tax]),
                            });
                        })
                        .catch(err => {
                            console.error("Failed to load tax rate", err);
                        })
                );
            }
        }

        if(sale.customer && sale.status === "COMPLETED") {
            promises.push(source.customers.get(sale.customer.uuid)
                .then((customer) => {
                    if(customer.allow_account_sales) {
                        if(accountUsed) {
                            results.account["start-balance"] = Math.round(
                                (customer.current_owing - accountUsed) * 100
                            ) / 100;
                            results.account["end-balance"] = customer.current_owing;
                            results.account["changed-balance"] = accountUsed;
                        } else {
                            results.account["current-balance"] = customer.current_owing;
                        }
                    }

                    if(getOption("outlet_loyalty") && customer.loyalty) {
                        const current = parseFloat(customer.loyalty_points.toString());

                        if(loyaltyEarned || loyaltyRedeemed) {
                            results.loyalty["after-sale"] = Math.round(current * 100) / 100;
                            results.loyalty["before-sale"] = Math.round(
                                (current - loyaltyEarned + loyaltyRedeemed) * 100
                            ) / 100;

                            if(loyaltyEarned) {
                                results.loyalty["earned"] = Math.round(loyaltyEarned * 100) / 100;
                            }

                            if(loyaltyRedeemed) {
                                results.loyalty["spent"] = Math.round(loyaltyRedeemed * 100) / 100;
                            }
                        } else {
                            results.loyalty["current-loyalty"] = current;
                        }
                    }
                })
                .catch(() => {
                    // Do nothing
                }));
        }

        if(results.eftpos.length && Array.isArray(results.eftpos)) {
            results.eftpos = results.eftpos.join("\n");
        } else {
            results.eftpos = "";
        }

        results.surcharges = Object.values(surchargeMap).map(surcharge => ({
            ...surcharge,
            amount: currency(surcharge.amount),
        }));

        if(sale.surcharge) {
            results.surcharges.push({
                name  : surchargeNames[sale.surcharge.id] ?? "Surchage",
                amount: currency(sale.surcharge.amount),
            });
        }

        promises.push(
            this._prepareReceiptDataTextFields(sale, source, additionalSaleData, isReprint)
                .then(text => {
                    results.text = text;
                })
        );

        await Promise.all(promises);

        return results;
    }

    public static async _prepareReceiptDataTextFields(
        sale: Required<SaleState>,
        source: BaseDataSource,
        additionalSaleData?: AdditionalSaleData,
        isReprint = false
    ): Promise<ReceiptSaleTextFields> {
        const results: ReceiptSaleTextFields = {
            invoiceNo       : sale.invoiceId,
            orderReference  : sale.orderReference,
            completedAt     : moment(sale.createdAt),
            user            : null,
            outlet          : null,
            register        : null,
            customer        : null,
            note            : sale.notes.sale || "",
            currentTimestamp: moment(),
            inTrainingMode  : isTrainingMode(),
            isReprint,
            sale,
        };

        const promises: Array<Promise<unknown>> = [];

        if(sale.registerId) {
            promises.push(source.registers.get(sale.registerId)
                .catch(err => {
                    if(err instanceof NotFoundException && typeof additionalSaleData !== "undefined") {
                        return {
                            name     : additionalSaleData.registerName,
                            outlet_id: additionalSaleData.outletId,
                        };
                    }

                    throw err;
                })
                .then(async register => {
                    results.register = {
                        name: register.name,
                    };

                    try {
                        const outlet = await source.outlets.get(register.outlet_id);

                        results.outlet = {
                            name          : outlet.name,
                            businessNumber: outlet.business_number,
                            contact       : {
                                addressStreet1 : outlet.contact?.address_street_1 ?? "",
                                addressStreet2 : outlet.contact?.address_street_2 ?? "",
                                addressSuburb  : outlet.contact?.address_suburb ?? "",
                                addressCity    : outlet.contact?.address_city ?? "",
                                addressPostcode: outlet.contact?.address_postcode ?? "",
                                addressRegion  : outlet.contact?.address_suburb ?? "",
                                addressCountry : outlet.contact?.address_country ?? "",
                                addressState   : outlet.contact?.address_state ?? "",
                                email          : outlet.contact?.email ?? "",
                                facebook       : outlet.contact?.facebook ?? "",
                                fax            : outlet.contact?.fax ?? "",
                                mobile         : outlet.contact?.mobile ?? "",
                                phone          : outlet.contact?.phone ?? "",
                                twitter        : outlet.contact?.twitter ?? "",
                                website        : outlet.contact?.website ?? "",
                            },
                        };
                    } catch(err) {
                        if(err instanceof NotFoundException && typeof additionalSaleData !== "undefined") {
                            results.outlet = {
                                name          : additionalSaleData.outletName,
                                businessNumber: "",
                                contact       : {
                                    addressStreet1 : "",
                                    addressStreet2 : "",
                                    addressSuburb  : "",
                                    addressCity    : "",
                                    addressPostcode: "",
                                    addressRegion  : "",
                                    addressCountry : "",
                                    addressState   : "",
                                    email          : "",
                                    facebook       : "",
                                    fax            : "",
                                    mobile         : "",
                                    phone          : "",
                                    twitter        : "",
                                    website        : "",
                                },
                            };
                        } else {
                            throw err;
                        }
                    }
                }));
        }

        if(sale.userId) {
            promises.push(source.users.get(sale.userId)
                .then((user) => {
                    results.user = {
                        name: user.name,
                    };
                })
                .catch(err => {
                    if(err instanceof NotFoundException) {
                        // Attempt to get the user from the server
                        return getUserFromServer(sale.userId as string)
                            .then(user => {
                                results.user = {
                                    name: user.name,
                                };
                            })
                            .catch(() => {
                                results.user = {
                                    name: "Shopfront",
                                };
                            });
                    }
                }));
        }

        if(sale.customer) {
            promises.push(
                ReceiptHelper.prepareReceiptCustomer(sale.customer.uuid).then(customer => results.customer = customer)
            );
        }

        await Promise.all(promises);

        return results;
    }

    public static async prepareReceiptCustomer(uuid: string): Promise<ReceiptCustomerTextFields | null> {
        if(!uuid) {
            return null;
        }

        try {
            const source = await determineDataSource();

            const customer = await source.customers.get(uuid);

            const {
                overrideCustomerGroup,
                customerGroup: customerGroupId,
                priceList: priceListId,
                ...value
            } = convertFromIndexedDBToGraphQL(customer);

            let customerGroup: null | { name: string; } = null;

            if(customerGroupId) {
                try {
                    customerGroup = await source.customerGroups.get(customerGroupId)
                        .then(customerGroup => ({
                            name: customerGroup.name,
                        }));
                } catch {
                    // Ignore
                }
            }

            let priceList: null | { name: string; } = null;

            if(priceListId) {
                try {
                    priceList = await source.priceLists.get(priceListId)
                        .then(priceList => ({
                            name: priceList.name,
                        }));
                } catch {
                    // Ignore
                }
            }

            const name = [];

            if(customer.first_name) {
                name.push(customer.first_name);
            }

            if(customer.last_name) {
                name.push(customer.last_name);
            }

            return {
                ...value,
                customerGroup,
                priceList,
                name: name.join(" "),
            };
        } catch {
            return null;
        }
    }

    public static createTextReceipt(
        components: Array<ComponentType>,
        data: Record<string, unknown>,
        width = 42,
        training = false
    ): string {
        return TextReceipt.generate(components, data, width, training);
    }
}

class TextReceipt {
    protected textReplacement: TextReplacement;

    constructor(
        protected data: Record<string, unknown>,
        protected training = false
    ) {
        this.textReplacement = new TextReplacement(this.data.text as Record<string, unknown>);
    }

    public createTextReceipt(
        components: Array<ComponentType>,
        width = 42
    ): string {
        const taxIndicators = getTaxIndicators(components);

        const results: Array<string> = [];

        for(let i = 0; i < components.length; i++) {
            const text = this.getTextFromComponent(
                components[i],
                width,
                taxIndicators
            );

            if(text) {
                results.push(...this.splitLines(text, width));
            }
        }

        if(this.training) {
            results.push(this.addWhitespace("TRAINING MODE", width, "center"));
        }

        return results.join("\n");
    }

    protected splitLines(value: string, width: number, splitOnWord = false): Array<string> {
        const results: Array<string> = [];

        const split = value.split("\n");

        for(let i = 0; i < split.length; i++) {
            if(split[i] === "") {
                results.push(" ".repeat(width));
            } else {
                results.push(...this.splitLine(split[i], width, splitOnWord));
            }
        }

        return results;
    }

    protected splitLine(value: string, width: number, splitOnWord = false): Array<string> {
        if(!splitOnWord) {
            let result: Array<string>;

            try {
                result = value.match(new RegExp(".{1," + width + "}", "g")) as Array<string>;
            } catch {
                result = [];
            }

            if(!result) {
                result = [];
            }

            return result;
        }

        const words = value.match(/\S+\s*/g);

        if(!words) {
            return this.splitLine(value, width, false);
        }

        const result = [];
        let current = "";

        for(let word of words) {
            if(current.length + word.length > width) {
                if(current.length > 0) {
                    result.push(current.trim());
                    current = "";
                }

                while(word.length > width) {
                    result.push(word.slice(0, width).trim());
                    word = word.slice(width);
                }
            }

            current += word;
        }

        if(current.length > 0) {
            result.push(current.trim());
        }

        return result;
    }

    protected addWhitespace(
        value: string,
        width: number,
        align: "left" | "right" | "center" = "left",
        join = ""
    ): string {
        const results = [];

        if(value) {
            const lines = this.splitLines(value, width);

            for(let i = 0; i < lines.length; i++) {
                let line = lines[i];

                const padding = width - line.length;

                if(padding) {
                    let leftPad = 0;
                    let rightPad = 0;

                    switch(align) {
                        case "center":
                            leftPad = Math.ceil(padding / 2);
                            rightPad = Math.floor(padding / 2);
                            break;
                        case "right":
                            leftPad = padding;
                            break;
                        default:
                            rightPad = padding; // Default is left aligned
                            break;
                    }

                    if(rightPad) {
                        line += " ".repeat(rightPad);
                    }

                    if(leftPad) {
                        line = " ".repeat(leftPad) + line;
                    }
                }

                results.push(line);
            }
        }

        return results.join(join);
    }

    protected getTextFromComponent(
        component: ComponentType,
        width: number,
        taxIndicators: Record<string, string>
    ): string {
        let results = "";

        switch(component.type) {
            case "heading":
                results = this.getHeadingFromTextComponent(component);
                break;
            case "text":
                results = this.getTextFromTextComponent(component, width);
                break;
            case "column":
                results = this.getTextFromColumns(component, width, taxIndicators);
                break;
            case "spacer":
                results = " ".repeat(
                    width * Math.floor(typeof component.value === "number" ? component.value / 16 : 0)
                );
                break;
            case "product":
                results = this.getTextFromProductsTable(component, width, taxIndicators);
                break;
            case "tax":
                results = this.getTextFromTaxTable(component, width);
                break;
            case "tax_indicator":
                results = this.getTextFromTaxIndicator(component);
                break;
            case "payment":
                results = this.getTextFromPaymentTable(component, width);
                break;
            case "loyalty":
                results = this.getTextFromLoyaltyTable(component, width);
                break;
            case "account":
                results = this.getTextFromAccountTable(component, width);
                break;
            case "total":
                results = this.getTextFromTotalTable(component, width);
                break;
            case "eftpos":
                results = this.getTextFromExternalReceipt(width);
                break;
            case "gift_cards":
                results = this.getTextFromGiftCardTable(component, width);
                break;
            case "surcharge":
                results = this.getTextFromSurchargeTable(component, width);
                break;
        }

        let align: ReceiptTextAlignment = "left";

        if(component.style && component.style.textAlign) {
            align = component.style.textAlign;
        }

        return this.addWhitespace(results, width, align);
    }

    protected getHeadingFromTextComponent(component: ComponentType): string {
        if(!isHeadingValue(component.value)) {
            return "";
        }

        if(!("status" in this.data)) {
            return "";
        }

        if(typeof this.data.status !== "string") {
            return "";
        }

        const status = this.data.status.toLowerCase() as keyof HeadingValueType;

        if(this.training) {
            return "TRAINING MODE RECEIPT";
        } else if(component.value[status]) {
            return component.value[status];
        }

        return "";
    }

    protected getTextFromTextComponent(component: ComponentType, width: number): string {
        if(typeof component.value !== "string") {
            return " ".repeat(width);
        }

        return this.removeHTMLFromText(
            this.textReplacement.parse(component.value),
            width
        );
    }

    protected removeHTMLFromText(text: string, width: number): string {
        // Get the groups of the styling
        const groups = text.split(/(\r\n|\r|\n)/g);

        // Handle the groups
        const results = [];

        for(let i = 0, l = groups.length; i < l; i++) {
            const group = groups[i].replace(/<br\/?>/g, "\n");

            if(group === "\n") {
                // Group is only a new line
                results.push("\n");
            } else if(document) {
                // Check if empty
                if(group.trim() === "") {
                    continue;
                }

                // Create new div
                const div = document.createElement("div");

                div.innerHTML = group;

                // Find the alignment
                let align: ReceiptTextAlignment = "left";
                const firstChild = div.firstChild;

                if(firstChild) {
                    align = ((firstChild as HTMLElement).style?.textAlign as ReceiptTextAlignment) || "left";
                }

                // Get the lines for the group
                const lines = div.innerText.split("\n");

                for(let i = 0, l = lines.length; i < l; i++) {
                    results.push(this.addWhitespace(lines[i], width, align));
                }
            } else {
                // Fallback to just removing tags
                results.push(group.replace(/<[^>]+>/g, ""));
            }
        }

        return results.join("\n");
    }

    protected getTextFromTable(data: Array<unknown>, widths: Record<string, number>): string {
        let results = "";

        for(let i = 0; i < data.length; i++) {
            const row = data[i];

            if(typeof row !== "object") {
                console.warn("Unexpected data in receipt table");
                continue;
            }

            if(row === null) {
                console.warn("Unexpected data in receipt table");
                continue;
            }

            const columns: Record<string, Array<string>> = {};
            let maxLines = 0;

            for(const column in widths) {
                if(Object.prototype.hasOwnProperty.call(widths, column)) {
                    let value = "";

                    if(column in row) {
                        value = (row as Record<string, string>)[column];
                    }

                    const lines = this.splitLines(value, widths[column]);

                    columns[column] = lines;
                    maxLines = Math.max(maxLines, lines.length);
                }
            }

            for(let j = 0; j < maxLines; j++) {
                for(const column in columns) {
                    if(Object.prototype.hasOwnProperty.call(columns, column)) {
                        if(columns[column][j]) {
                            results += this.addWhitespace(columns[column][j], widths[column]);
                        } else {
                            results += " ".repeat(widths[column]);
                        }
                    }
                }
            }
        }

        return results;
    }

    protected getTextFromTaxTable(component: ComponentType, width: number): string {
        const { tax } = this.data;

        if(!Array.isArray(tax)) {
            return "";
        }

        if(!hasColumnsValue(component.value)) {
            return "";
        }

        const widths: Record<string, number> = {};

        const { columns } = component.value;

        let remaining = width;
        let lastCol = null;

        for(let i = 0; i < columns.length; i++) {
            const col = columns[i];

            if(col.enabled) {
                let width = 0;

                if(col.column === "total") {
                    width = 7;
                }

                widths[col.column] = width;
                remaining -= width;

                lastCol = col.column;
            }
        }

        if(widths["tax"] === 0) {
            widths["tax"] = remaining;
        } else if(lastCol) {
            widths[lastCol] += remaining;
        } else {
            return "";
        }

        return this.getTextFromTable(tax, widths);
    }

    protected getTextFromPaymentTable(component: ComponentType, width: number): string {
        const { payments } = this.data;

        if(!Array.isArray(payments)) {
            return "";
        }

        const widths: Record<string, number> = {};

        if(!hasColumnsValue(component.value)) {
            return "";
        }

        const { columns } = component.value;

        let remaining = width;
        let lastCol = null;

        for(let i = 0; i < columns.length; i++) {
            const col = columns[i];

            if(col.enabled) {
                let width = 0;

                if(col.column === "amount") {
                    width = 7;
                }

                widths[col.column] = width;
                remaining -= width;

                lastCol = col.column;
            }
        }

        if(widths["payment"] === 0) {
            widths["payment"] = remaining;
        } else if(lastCol) {
            widths[lastCol] += remaining;
        } else {
            return "";
        }

        // TODO: ADD CASHOUT LINE AFTER PAYMENT LINE

        return this.getTextFromTable(payments, widths);
    }

    protected getTextFromLoyaltyTable(component: ComponentType, width: number): string {
        const { loyalty } = this.data;

        if(!isRecord(loyalty)) {
            return "";
        }

        if(!hasColumnsValue(component.value)) {
            return "";
        }

        let results = "";

        const rows = component.value.columns;

        for(let i = 0; i < rows.length; i++) {
            const row = rows[i];

            if(!(row.column in loyalty)) {
                continue;
            }

            const castKey = row.column as keyof typeof loyalty;

            if(row.enabled && loyalty[castKey] !== false) {
                results += this.addWhitespace(row.name, width - 9);
                results += this.addWhitespace("" + loyalty[castKey], 9, "right");
            }
        }

        return results;
    }

    protected getTextFromAccountTable(component: ComponentType, width: number): string {
        const { account } = this.data;

        if(!isRecord(account)) {
            return "";
        }

        if(!hasColumnsValue(component.value)) {
            return "";
        }

        let results = "";

        let maxWidth = 0;
        const rows = component.value.columns;
        const values: Record<string, string> = {};

        for(let i = 0; i < rows.length; i++) {
            const row = rows[i];

            if(!(row.column in account)) {
                continue;
            }

            const castKey = row.column as keyof typeof account;

            if(row.enabled && account[castKey] !== false) {
                const amount = currency(account[castKey] as number);

                maxWidth = Math.max(amount.length, maxWidth);
                values[row.column] = amount;
            }
        }

        for(let i = 0; i < rows.length; i++) {
            const row = rows[i];

            if(row.enabled && typeof values[row.column] === "string") {
                results += this.addWhitespace(row.name, width - maxWidth);
                results += this.addWhitespace(values[row.column], maxWidth);
            }
        }

        return results;
    }

    protected getTextFromTotalTable(component: ComponentType, width: number): string {
        const { total } = this.data;

        if(!isRecord(total)) {
            return "";
        }

        if(!hasColumnsValue(component.value)) {
            return "";
        }

        let results = "";

        const rows = component.value.columns;

        for(let i = 0; i < rows.length; i++) {
            const row = rows[i];

            if(!(row.column in total)) {
                continue;
            }

            if(row.enabled && total[row.column]) {
                results += this.addWhitespace(row.name, width - 7);
                results += this.addWhitespace(currency(total[row.column] as number), 7);
            }
        }

        return results;
    }

    protected getTextFromTaxIndicator(component: ComponentType): string {
        const { indicators, tax } = this.data;

        if(!Array.isArray(tax)) {
            return "";
        }

        if(!isRecord(indicators)) {
            return "";
        }

        const value = component.value;

        if(!isTaxIndicatorValue(value)) {
            return "";
        }

        let results = "";

        if(value.tax in indicators) {
            let taxName = "No Tax";

            if(value.tax) {
                const found = tax.find((el) => {
                    return el.id === value.tax;
                });

                if(found) {
                    taxName = found.tax;
                }
            }

            results = value.indicator + " " + value.text + " " + taxName;
        }

        return results;
    }

    protected getTextFromColumns(
        component: ComponentType,
        width: number,
        taxIndicators: Record<string, string>
    ): string {
        if(!isColumnComponentType(component)) {
            return "";
        }

        let results = "";
        const cols = [];

        let remainder = 0;
        let maxLines = 0;

        for(let i = 0, l = component.value.length; i < l; i++) {
            const column = component.value[i];

            const columnWidth = width * column.width;
            let colCharWidth = Math.floor(columnWidth);

            remainder += Math.round(columnWidth * 100 - colCharWidth * 100) / 100;

            const extra = Math.floor(remainder);

            remainder -= extra;
            colCharWidth += extra;

            let colText = "";

            // Add column spacer
            let wid = colCharWidth;

            if(i !== 0) {
                wid = Math.max(0, wid - 1);
            }

            for(let j = 0, n = column.components.length; j < n; j++) {
                colText += this.getTextFromComponent(column.components[j], wid, taxIndicators);
            }

            const lines = this.splitLines(colText, wid);

            maxLines = Math.max(maxLines, lines.length);

            cols.push({
                width: colCharWidth,
                lines: lines,
            });
        }

        for(let i = 0; i < maxLines; i++) {
            for(let j = 0; j < cols.length; j++) {
                const column = cols[j];

                if(i < column.lines.length) {
                    let line = column.lines[i];

                    if(column.width !== line.length) {
                        line = " " + line;
                    }

                    results += line;
                } else {
                    results += " ".repeat(column.width);
                }
            }
        }

        return results;
    }

    protected getTextFromProductsTable(
        component: any,
        width: number,
        taxIndicators: Record<string, string>
    ): string {
        const { products } = this.data;

        if(!Array.isArray(products)) {
            return "";
        }

        let results = "";

        const widths: Record<string, number> = {};

        const { columns, showDiscountReason, showProductNote } = component.value;

        let remaining = width || 40;
        let lastCol = null;

        for(let i = 0, l = columns.length; i < l; i++) {
            const col = columns[i];

            if(col.enabled) {
                let width = 0;

                if(col.column === "qty") {
                    width = 5;
                } else if(col.column === "price") {
                    width = 7;
                }

                widths[col.column] = width;
                remaining -= width;

                lastCol = col.column;
            }
        }

        if(widths["product"] === 0) {
            widths["product"] = remaining;
        } else if(lastCol) {
            widths[lastCol] += remaining;
        } else {
            return "";
        }

        for(let i = 0, l = columns.length; i < l; i++) {
            const col = columns[i];

            if(col.enabled) {
                results += this.addWhitespace(
                    col.name,
                    widths[col.column],
                    (col.column === "price" ? "right" : "left")
                );
            }
        }

        for(let i = 0, l = products.length; i < l; i++) {
            const columnRows: Record<string, Array<string>> = {};
            let maxLines = 0;

            for(const column in widths) {
                if(Object.prototype.hasOwnProperty.call(widths, column)) {
                    const width = widths[column];

                    if(width) {
                        let suffix = "";

                        if(column === "product") {
                            if(taxIndicators[products[i].tax]) {
                                suffix += taxIndicators[products[i].tax];
                            }

                            if(products[i].surcharge) {
                                suffix += `[${products[i].surcharge}]`;
                            }
                        }

                        const lines = this.splitLines(products[i][column] + suffix, width, column === "product");

                        maxLines = Math.max(maxLines, lines.length);

                        columnRows[column] = lines;
                    }
                }
            }

            for(let j = 0; j < maxLines; j++) {
                let line = "";

                for(let k = 0; k < columns.length; k++) {
                    const col = columns[k];

                    if(col.enabled) {
                        if(Array.isArray(columnRows[col.column]) && j < columnRows[col.column].length) {
                            line += this.addWhitespace(
                                columnRows[col.column][j],
                                widths[col.column],
                                (col.column === "price" ? "right" : "left")
                            );
                        } else {
                            line += " ".repeat(widths[col.column]);
                        }
                    }
                }

                results += line;
            }

            if(showProductNote && products[i].note) {
                results += this.addWhitespace(" - " + products[i].note, width, "left");
            }

            if(showDiscountReason && products[i]["discount-reason"]) {
                results += this.addWhitespace(" - " + products[i]["discount-reason"], width, "left");
            }

            // Add components to receipts
            if(products[i].components?.length) {
                const priceWidth = widths?.price || 0;
                const componentWidth = width - priceWidth;

                for(let j = 0, m = products[i].components.length; j < m; j++) {
                    const icon = products[i].components[j].type === "added" ? "+" : "-";

                    const componentLines = this.splitLines(` ${icon} ${products[i].components[j].name}`, componentWidth);
                    let priceRows: Array<string> = [];

                    if(priceWidth) {
                        priceRows = this.splitLines(currency(products[i].components[j].price), priceWidth);
                    }

                    for(let k = 0, n = Math.max(componentLines.length, priceRows.length); k < n; k++) {
                        let line = "";

                        line += componentLines[i] ?
                            this.addWhitespace(
                                componentLines[i],
                                componentWidth,
                                "left"
                            ) :
                            " ".repeat(componentWidth);

                        if(priceWidth) {
                            line += priceRows[i] ?
                                this.addWhitespace(
                                    priceRows[i],
                                    priceWidth,
                                    "right"
                                ) :
                                " ".repeat(priceWidth);
                        }

                        results += line;
                    }
                }
            }
        }

        return results;
    }

    protected getTextFromExternalReceipt(width: number): string {
        const { eftpos } = this.data;

        if(typeof eftpos !== "string") {
            return "";
        }

        const lines = eftpos.split("\n");
        let maxWidth = 0;

        for(let i = 0, l = lines.length; i < l; i++) {
            const line = lines[i];

            maxWidth = Math.max(maxWidth, line.length);
        }

        maxWidth = Math.min(width, maxWidth);

        const results = this.addWhitespace(eftpos, maxWidth, "left", "\n");

        return this.addWhitespace(results, width, "center");
    }

    protected getTextFromGiftCardTable(component: ComponentType, width: number): string {
        if(!hasColumnsValue(component.value)) {
            return "";
        }

        const { giftCards } = this.data;
        const rows = component.value.columns;

        let allDisabled = true;
        let lastRow = 0;

        for(let i = 0, l = rows.length; i < l; i++) {
            if(rows[i].enabled) {
                allDisabled = false;
                lastRow = i;
            }
        }

        if(allDisabled) {
            return "";
        }

        if(!Array.isArray(giftCards) || !giftCards.length) {
            return "";
        }

        let results = "";

        for(let i = 0, l = giftCards.length; i < l; i++) {
            const giftCard = giftCards[i] as GiftCardUsedType;

            results += this.addWhitespace("Gift Card", width - 9);
            results += this.addWhitespace(giftCard.code, 9, "right");

            for(let j = 0, m = rows.length; j < m; j++) {
                const row = rows[j];

                if(!row.enabled) {
                    continue;
                }

                const key = row.column as keyof GiftCardUsedType;

                if(!(key in giftCard)) {
                    continue;
                }

                results += this.addWhitespace(rows[j].name, width - 9);
                results += this.addWhitespace(`${giftCard[key]}`, 9, "right");

                if(j === lastRow) {
                    results += " ".repeat(width);
                }
            }
        }

        return results;
    }

    protected getTextFromSurchargeTable(component: ComponentType, width: number): string {
        const { surcharges } = this.data;

        if(!Array.isArray(surcharges)) {
            return "";
        }

        const widths: Record<string, number> = {};

        if(!hasColumnsValue(component.value)) {
            return "";
        }

        const formatted: Array<{ name: string; amount: string; }> = [];

        for(const row of surcharges) {
            formatted.push({
                name: (
                    typeof row.indicator === "string" ?
                        `[${row.indicator}] ${row.name as string}` :
                        row.name as string
                ),
                amount: row.amount as string,
            });
        }

        const { columns } = component.value;

        let remaining = width;
        let lastCol = null;

        for(let i = 0; i < columns.length; i++) {
            const col = columns[i];

            if(col.enabled) {
                let width = 0;

                if(col.column === "amount") {
                    width = 7;
                }

                widths[col.column] = width;
                remaining -= width;

                lastCol = col.column;
            }
        }

        if(widths["name"] === 0) {
            widths["name"] = remaining;
        } else if(lastCol) {
            widths[lastCol] += remaining;
        } else {
            return "";
        }

        return this.getTextFromTable(formatted, widths);
    }

    public static generate(
        components: Array<ComponentType>,
        data: Record<string, unknown>,
        width = 42,
        training = false
    ): string {
        const instance = new TextReceipt(data, training);

        return instance.createTextReceipt(components, width);
    }
}

export default ReceiptHelper;
